/**
 * MarketMomentum Component (renamed to "Market Trend")
 *
 * Displays market trend via:
 * - 20-day sparkline of SPY price with SMA-200 overlay (gray dashed)
 * - Status label (BULLISH / NEUTRAL / BEARISH) based on price vs SMA-200 position
 * - Compact SMA-200 info row below chart
 */

import React, { useState, useEffect } from 'react';
import { ResponsiveContainer, LineChart, Line, YAxis } from 'recharts';
import { apiService } from '../../../services/api';
import InfoTooltip from '../../common/InfoTooltip';

interface ChartDataPoint {
  timestamp: number;
  close: number;
  sma_200: number | null;
}

interface SpyTrendData {
  status: string;
  current_price?: number;
  sma_200?: number;
  trend?: 'bullish' | 'bearish' | 'neutral';
  percent_above_sma?: number;
  slope_direction?: 'up' | 'down' | 'flat' | null;
}

interface MarketMomentumProps {
  loading?: boolean;
  spyTrend?: SpyTrendData | null;
}

const MarketMomentum: React.FC<MarketMomentumProps> = ({ loading: parentLoading, spyTrend }) => {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSpyData = async () => {
      setLoading(true);
      setError(false);

      try {
        // Fetch last 30 days to get 20 trading days
        const toDate = new Date();
        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 30);

        const fromDateStr = fromDate.toISOString().split('T')[0];
        const toDateStr = toDate.toISOString().split('T')[0];

        const response = await apiService.getChartDataWithIndicators(
          'SPY',
          fromDateStr,
          toDateStr,
          false
        );

        if (response.status === 'success' && response.data.results.length > 0) {
          // Take the last 20 data points
          const results = response.data.results.slice(-20);
          const transformed = results
            .filter(d => d.close !== null)
            .map(d => ({
              timestamp: d.timestamp,
              close: d.close || 0,
              sma_200: d.sma_200
            }));

          setChartData(transformed);
        }
      } catch (err) {
        console.error('Failed to fetch SPY momentum data:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSpyData();
  }, []);

  // Determine direction from price vs SMA-200 position
  const percentAboveSma = spyTrend?.percent_above_sma ?? 0;
  const slopeDirection = spyTrend?.slope_direction ?? null;  // null = unknown slope

  // Trader theme: the trend is a state, not P&L — label and line stay neutral grey.
  const trendColor = '#a2a7ae'; // --tk-t2
  let trendArrow = '→';
  let trendLabel = 'NEUTRAL';

  if (percentAboveSma > 0 && slopeDirection === 'up') {
    trendArrow = '↗';
    trendLabel = 'BULLISH';
  } else if (percentAboveSma < 0) {
    trendArrow = '↘';
    trendLabel = 'BEARISH';
  }

  // Current price and daily change from chart data
  const currentPrice = chartData.length > 0 ? chartData[chartData.length - 1].close : spyTrend?.current_price ?? 0;
  const prevClose = chartData.length > 1 ? chartData[chartData.length - 2].close : null;
  const dailyChange = prevClose ? currentPrice - prevClose : null;
  const dailyChangePct = prevClose ? ((currentPrice - prevClose) / prevClose) * 100 : null;

  let changeClass = 'db-zero';
  if (dailyChange !== null) {
    changeClass = dailyChange > 0 ? 'db-gain' : dailyChange < 0 ? 'db-loss' : 'db-zero';
  }

  const isLoading = parentLoading || loading;

  if (isLoading) {
    return (
      <div className="market-context-card momentum-card">
        <div className="market-context-card-header">
          <div className="market-context-card-title">
            Market Trend
            <InfoTooltip text="Shows if SPY is trading above or below its 200-day moving average. Bullish = price >2% above SMA-200, Bearish = price >2% below." />
          </div>
        </div>
        <div className="market-context-loading">Loading...</div>
      </div>
    );
  }

  if (error || chartData.length === 0) {
    return (
      <div className="market-context-card momentum-card">
        <div className="market-context-card-header">
          <div className="market-context-card-title">
            Market Trend
            <InfoTooltip text="Shows if SPY is trading above or below its 200-day moving average. Bullish = price >2% above SMA-200, Bearish = price >2% below." />
          </div>
        </div>
        <div className="market-context-loading">Data unavailable</div>
      </div>
    );
  }

  // Compute Y-axis domain that includes both close and sma_200
  const allValues = chartData.flatMap(d => {
    const vals = [d.close];
    if (d.sma_200 != null) vals.push(d.sma_200);
    return vals;
  });
  const yMin = Math.min(...allValues);
  const yMax = Math.max(...allValues);
  const yPad = (yMax - yMin) * 0.1 || 1;

  return (
    <div className="market-context-card momentum-card">
      <div className="market-context-card-header">
        <div className="market-context-card-title">
          Market Trend
          <InfoTooltip text="Shows if SPY is trading above or below its 200-day moving average. Bullish = price >2% above SMA-200, Bearish = price >2% below." />
        </div>
        <div className="market-context-status-pill">
          {trendArrow} {trendLabel}
        </div>
      </div>
      <div className="momentum-container">
        {/* Sparkline Chart with SMA-200 overlay */}
        <div className="momentum-chart">
          <ResponsiveContainer width="100%" height={64}>
            <LineChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
              <YAxis domain={[yMin - yPad, yMax + yPad]} hide />
              <Line
                type="monotone"
                dataKey="close"
                stroke={trendColor}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="sma_200"
                stroke="#6f747c"
                strokeWidth={1}
                strokeDasharray="4 3"
                dot={false}
                isAnimationActive={false}
                connectNulls
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Price info row */}
        <div className="trend-sma-row">
          <span className="trend-sma-label">SPY ${currentPrice.toFixed(2)}</span>
          {dailyChange !== null && dailyChangePct !== null && (
            <span className={`trend-sma-change ${changeClass}`}>
              {dailyChange > 0 ? '+' : ''}{dailyChange.toFixed(2)} ({dailyChangePct > 0 ? '+' : ''}{dailyChangePct.toFixed(2)}%)
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketMomentum;
